<template>
  <v-modal id="pax-create-modal" class-name="modal-fullscreen-sm-down" size="lg" @closed="$emit('closed')">
    <modal-header>
      <modal-title>
        {{ $t("clientTour.createNewPax") }}
      </modal-title>
    </modal-header>

    <modal-body>
      <form id="case-client-create-form" class="d-flex flex-column justify-content-between h-100">
        <fieldset>
          <legend class="visually-hidden">{{ $t("client.basicInformation") }}</legend>

          <v-form-input
            id="case-pax-create-name"
            v-model="name"
            :label="`${$t('general.shared.name')}*`"
            :yup-errors-variable="errors.name"
            autocomplete="off"
            class="mb-8"
            form-type="outline"
          />

          <v-form-input
            id="case-pax-create-email"
            v-model="email"
            :label="`${$t('general.shared.email')}*`"
            :yup-errors-variable="errors.email"
            autocomplete="off"
            class="mb-8"
            form-type="outline"
            type="email"
          />
        </fieldset>

        <v-button
          :disabled="!meta.valid || accepted"
          :is-loading="accepted"
          class="btn-submit-form btn-icon w-fit align-self-end"
          size="sm"
          variant="primary"
          @click="createPax"
        >
          {{ $t("general.button.create") }}
          <v-icon icon="arrow-right" size="sm" space="ms-12" />
        </v-button>
      </form>
    </modal-body>
  </v-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useField } from "vee-validate";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { VFormInput } from "@uniqoders/form";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import useFormValidation from "@/helpers/form";
import useConcepts from "@/helpers/Concepts";
import api from "@/api";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";

export default defineComponent({
  name: "VPaxCreateModal",
  components: {
    ModalBody,
    ModalTitle,
    ModalHeader,
    VModal,
    VFormInput,
    VButton,
    VIcon,
  },
  emits: ["closed", "client", "reloadCase"],
  props: {
    agency: {
      type: String,
      required: true,
    },
    caseDay: {
      type: {} as any,
      required: true,
    },
    caseId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const { t } = useI18n();

    const rules = yup.object({
      name: yup.string().required().label(t("general.shared.name")),
      email: yup.string().email().required().label(t("general.shared.email")),
    });

    const form = useFormValidation(rules);

    const { value: name } = useField("name");
    const { value: email } = useField("email");

    return {
      ...useConcepts(),
      ...form,
      name,
      email,
    };
  },
  methods: {
    async createPax() {
      try {
        this.toggleAccepted();

        const data: any = {
          name: this.name,
          email: this.email,
        };

        if (this.caseId !== null) {
          await api.case.caseAddPax(this.agency, this.caseId, data);
        } else {
          await api.case.caseDayItemAddPax(this.agency, this.caseDay?.case_id, this.caseDay?.id, data);
        }

        this.$toast.success(this.$t("general.shared.savedChanges"));

        this.$emit("reloadCase");
        this.$emit("closed");

        this.resetForm();

        (this.$refs["case-client-create-address-form-ref"] as any).clearForm();

        this.toggleAccepted();
      } catch (e: any) {
        this.toggleAccepted();

        console.error(e.response.data);

        this.$toast.error(e.response.data.message);
      }
    },
  },
});
</script>
